import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { DateShortcutField } from '@/components/forms/DateShortcutField';
import { ValidationError, getApiErrorMessage, getValidationErrors } from '@/lib/validationErrors';
import { useContract } from '@/hooks/forestry/useContract';
import type { MenuActionExtraDialogProps } from '@/config/menuActionExtras';

/** The contract's pre-calculation, if it has one (a contract has one in this version). */
function useContractPreCalculationId(contractId: string, enabled = true) {
  const { data } = useQuery({
    queryKey: ['contract-pre-calculation', contractId],
    queryFn: async () => (await apiClient.get<{ items: { id: string }[] }>(
      '/api/forestry/precalculation', { params: { contractId, maxResultCount: 1 } })).data.items[0]?.id ?? null,
    enabled: enabled && !!contractId,
  });
  return data;
}

/** The menu item's text: "Open pre-calculation" when the contract has one, else the registered "Create pre-calculation". */
export function PreCalculationActionLabel({ entityId, label }: { entityId: string; label: string }) {
  const { t } = useTranslation();
  const existingId = useContractPreCalculationId(entityId);
  return <>{existingId ? t('Forestry.OpenPreCalculation.Title', { defaultValue: 'Open pre-calculation' }) : label}</>;
}

/**
 * "Create pre-calculation" on a contract (SkogligKalkylSpecV5, Calculating the value side): asks the price date, offering
 * the contract date, then POST /api/forestry/precalculation/calculate and opens the contract's pre-calculation — the
 * one it already has, as it is, when it has one.
 */
export function CreatePreCalculationDialog({ entityId, open, onClose }: MenuActionExtraDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: contract } = useContract(entityId, { enabled: open });
  const existingId = useContractPreCalculationId(entityId, open);
  const [priceDate, setPriceDate] = useState('');
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);

  // A contract that has a pre-calculation opens it: there is nothing to ask.
  useEffect(() => {
    if (open && existingId) {
      onClose();
      navigate(`/forestry/precalculations/${existingId}`);
    }
  }, [open, existingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The contract date until the user picks another (Forestry: the price date is the contract date for now).
  useEffect(() => {
    if (open && contract?.contractDate) setPriceDate(String(contract.contractDate).slice(0, 10));
  }, [open, contract?.contractDate]);

  const calculate = useMutation({
    mutationFn: async () =>
      (await apiClient.post<{ id: string }>('/api/forestry/precalculation/calculate', { contractId: entityId, priceDate })).data,
    onSuccess: (calculation) => {
      void queryClient.invalidateQueries({ queryKey: ['contract-pre-calculation', entityId] });
      handleClose();
      navigate(`/forestry/precalculations/${calculation.id}`);
    },
    onError: (error: unknown) => {
      const errors = getValidationErrors(error);
      if (errors.length > 0) {
        setApiValidationErrors(errors);
        return;
      }
      const message = getApiErrorMessage(error) ?? t('common.genericError', { defaultValue: 'An error occurred' });
      setApiValidationErrors([{ field: 'form', message, instancePath: '#', members: [] }]);
    },
  });

  const handleClose = () => {
    setPriceDate('');
    setApiValidationErrors([]);
    onClose();
  };

  return (
    // The field's calendar opens in a popper outside the dialog, which must not pull the focus back from it.
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth disableEnforceFocus>
      <DialogTitle>{t('Forestry.CreatePreCalculation.Title', { defaultValue: 'Create pre-calculation' })}</DialogTitle>
      <DialogContent>
        <DialogValidationErrors errors={apiValidationErrors} onClear={() => setApiValidationErrors([])} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('Forestry.CreatePreCalculation.Intro', {
            defaultValue: 'The price date chooses the calculation template and the price lists. A contract that already has a pre-calculation opens it.',
          })}
        </Typography>
        <DateShortcutField
          label={t('DisplayName:PreCalculation.priceDate', { defaultValue: 'Price date' })}
          required
          value={priceDate}
          onChange={(value) => setPriceDate(value ?? '')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
        <Button variant="contained" disabled={!priceDate || calculate.isPending} onClick={() => calculate.mutate()}>
          {t('Forestry.CreatePreCalculation.Confirm', { defaultValue: 'Calculate' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
